import { Icons } from 'src/assets/fonts/Icons/icons';
import { BRANCH_DELIVERY_TYPE, DELIVERY_TYPE_PICKER_OPTION } from 'src/constants/deliveryTypes';
import { PAYMENT_TYPES } from 'src/constants/main';
import { IBranch, IBranchPaymentType } from 'src/types/branches';
import { booleanToNumber } from 'src/utils/helpers';
import {
  IBranchOrderTypeOption,
  IBranchPaymentTypeOption,
  WORKING_HOURS_ORDER_TYPE,
} from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/types/branchEdit';

/**
 * Check if every type status has falsy value
 *
 * @param types
 * @returns {boolean}
 */
export const isEveryTypeDisabled = <T extends { status: boolean | number }>(types: T[]): boolean => {
  return types.every((type) => !type.status);
};

/**
 * Get branch delivery type from editable options
 *
 * @param {IBranchOrderTypeOption[]} options
 * @returns {BRANCH_DELIVERY_TYPE | undefined}
 */
export const getBranchDeliveryTypeByOptions = (options: IBranchOrderTypeOption[]): BRANCH_DELIVERY_TYPE | string => {
  const pickupOption = options.find((option) => option.id === DELIVERY_TYPE_PICKER_OPTION.PICKUP);
  const deliveryOption = options.find((option) => option.id === DELIVERY_TYPE_PICKER_OPTION.DELIVERY);

  switch (true) {
    case pickupOption?.status && !deliveryOption?.status:
      return BRANCH_DELIVERY_TYPE.PICKUP;
    case deliveryOption?.status && !pickupOption?.status:
      return BRANCH_DELIVERY_TYPE.DELIVERY;
    case pickupOption?.status && deliveryOption?.status:
      return BRANCH_DELIVERY_TYPE.BOTH;
    default:
      return '';
  }
};

/**
 * Get branch order type option with updated availability status
 *
 * @param {IBranchOrderTypeOption} orderTypeOption
 * @param {BRANCH_DELIVERY_TYPE} deliveryType - Common or subscription
 * @param {boolean} isCurbside
 * @param {boolean} isEmenu
 * @returns {IBranchOrderTypeOption}
 */
export const getUpdatedBranchOrderTypeOption = (
  orderTypeOption: IBranchOrderTypeOption,
  deliveryType: BRANCH_DELIVERY_TYPE,
  isCurbside: number,
  isEmenu: boolean,
  isPickup: boolean
): IBranchOrderTypeOption => {
  let isOrderTypeEnabled: boolean;

  switch (orderTypeOption.id) {
    case DELIVERY_TYPE_PICKER_OPTION.PICKUP:
      isOrderTypeEnabled = [BRANCH_DELIVERY_TYPE.PICKUP, BRANCH_DELIVERY_TYPE.BOTH].includes(deliveryType) && !Boolean(!isPickup && isCurbside);
      break;
    case DELIVERY_TYPE_PICKER_OPTION.DELIVERY:
      isOrderTypeEnabled = [BRANCH_DELIVERY_TYPE.DELIVERY, BRANCH_DELIVERY_TYPE.BOTH].includes(deliveryType);
      break;
    case DELIVERY_TYPE_PICKER_OPTION.CURBSIDE:
      isOrderTypeEnabled = !!isCurbside;
      break;
    case DELIVERY_TYPE_PICKER_OPTION.DINE_IN:
      isOrderTypeEnabled = isEmenu;
      break;
  }

  return { ...orderTypeOption, status: isOrderTypeEnabled };
};

/**
 * Get branch payment type option with updated availability status
 *
 * @param {IBranchPaymentTypeOption} paymentTypeOption - Common or subscription
 * @param {IBranch['payment_types']} branchPaymentTypes
 * @return {IBranchPaymentTypeOption}
 */
export const getUpdatedBranchPaymentTypeOption = (
  paymentTypeOption: IBranchPaymentType,
  branchPaymentTypes: IBranch['payment_types']
): IBranchPaymentTypeOption => {
  const isPaymentTypeEnabled = branchPaymentTypes.some((branchPaymentType) => branchPaymentType.id === paymentTypeOption.id);
  return { ...paymentTypeOption, status: isPaymentTypeEnabled };
};

/**
 * Get payment types with default option at first position
 *
 * @param paymentTypes
 * @param defaultPaymentTypeId
 * @return {IBranchPaymentTypeOption[]}
 */
export const getResortedPaymentTypes = (
  paymentTypes: IBranchPaymentType[],
  defaultPaymentTypeId: PAYMENT_TYPES | undefined
): IBranchPaymentType[] => {
  const copyPaymentTypes = [...paymentTypes];
  const defaultTypeIndex = paymentTypes.findIndex((paymentType) => paymentType.id === defaultPaymentTypeId);

  copyPaymentTypes.push(...copyPaymentTypes.splice(0, defaultTypeIndex));

  return copyPaymentTypes;
};

/**
 * Get payment types with is_default param
 *
 * @param paymentTypes
 * @return {IBranchPaymentTypeOption[]}
 */
export const getPaymentTypesWithDefault = (paymentTypes: IBranchPaymentTypeOption[]): IBranchPaymentTypeOption[] => {
  return paymentTypes.map((paymentType, idx) => ({ ...paymentType, is_default: booleanToNumber(!idx) }));
};

export const getWorkingHoursOrderTypeIcons = (orderType: WORKING_HOURS_ORDER_TYPE): Icons[] => {
  switch (orderType) {
    case WORKING_HOURS_ORDER_TYPE.BOTH:
      return [Icons.Delivery, Icons.ShoppingBag, Icons.Curbside, Icons.DineIn];
    case WORKING_HOURS_ORDER_TYPE.PICKUP:
      return [Icons.ShoppingBag, Icons.Curbside, Icons.DineIn];
    case WORKING_HOURS_ORDER_TYPE.DELIVERY:
      return [Icons.Delivery];
    default:
      return [];
  }
};
